/**
 * 围栏 Tab —— 全量电子围栏分页列表（按当前仓库配送区邮编范围）
 *
 * 复用详情页表格列（buildGeofenceColumns）；支持围栏状态/名称筛选、分页、审核/删除，
 * 点击 ID / 查看在右侧抽屉打开对应 zipcode 的地图详情。
 */
import React, { ChangeEvent, useCallback, useState } from 'react';
import { Button, Select, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import { Table } from 'antd';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { useFilterLabelClassName, useFilterContainerClassName, useFilterInputContainerClassName } from '../../../../utils/languageUtils';
import { buildGeofenceColumns } from './GeofenceColumns';
import { ZipcodeMapGeofenceType } from '../graphql/queries/getZipcodeGeofenceListing';
import {
    GET_ZIPCODE_GEOFENCE_PAGE_LISTING,
    GetZipcodeGeofencePageListingResponse,
    ZipcodeGeofenceFiltersType,
} from '../graphql/queries/getZipcodeGeofencePageListing';
import { AUDIT_ZIPCODE_GEOFENCE, AuditZipcodeGeofenceResponse } from '../graphql/mutations/auditZipcodeGeofence';
import { DELETE_ZIPCODE_GEOFENCE, DeleteZipcodeGeofenceResponse } from '../graphql/mutations/deleteZipcodeGeofence';
import ZipcodeMapDrawer from './ZipcodeMapDrawer';

type GeofenceFilterState = {
    zipcode?: string;
    geofenceStatus?: string;
    geofenceName?: string;
};

const GeofenceListTab: React.FC = () => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();

    const [tempFilters, setTempFilters] = useState<GeofenceFilterState>({});
    const [filters, setFilters] = useState<GeofenceFilterState>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [drawerZipcode, setDrawerZipcode] = useState<string | null>(null);

    const [auditModalVisible, setAuditModalVisible] = useState(false);
    const [currentAuditRecord, setCurrentAuditRecord] = useState<ZipcodeMapGeofenceType | null>(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [currentDeleteRecord, setCurrentDeleteRecord] = useState<ZipcodeMapGeofenceType | null>(null);

    const queryFilters: ZipcodeGeofenceFiltersType = {
        ...filters,
        warehouseId: currentWarehouse?.id ? Number(currentWarehouse.id) : undefined,
    };

    const { data, loading, refetch } = useQuery<GetZipcodeGeofencePageListingResponse>(
        GET_ZIPCODE_GEOFENCE_PAGE_LISTING,
        {
            variables: {
                filters: queryFilters,
                pageRequest: {
                    page: currentPage,
                    size: pageSize,
                    sortBy: 'createdAt',
                    sortDirection: 'DESC',
                },
            },
            skip: !currentWarehouse?.id,
            fetchPolicy: 'cache-and-network',
        }
    );

    const listData = data?.getZipcodeGeofencePageListing?.content || [];
    const totalElements = data?.getZipcodeGeofencePageListing?.totalElements || 0;

    const reloadList = useCallback(() => {
        refetch();
    }, [refetch]);

    const [auditGeofence, { loading: auditLoading }] = useMutation<AuditZipcodeGeofenceResponse>(
        AUDIT_ZIPCODE_GEOFENCE,
        {
            onCompleted: (res) => {
                if (res.auditZipcodeGeofence) {
                    message.success(t('wms.zipcodeMapGeofence.messages.auditSuccess'));
                    setAuditModalVisible(false);
                    setCurrentAuditRecord(null);
                    reloadList();
                } else {
                    message.error(t('wms.zipcodeMapGeofence.messages.auditFailed'));
                }
            },
            onError: (err) => {
                message.error(t('wms.zipcodeMapGeofence.messages.auditFailedWithReason', { reason: err.message }));
            },
        }
    );

    const [deleteGeofence, { loading: deleteLoading }] = useMutation<DeleteZipcodeGeofenceResponse>(
        DELETE_ZIPCODE_GEOFENCE,
        {
            onCompleted: (res) => {
                if (res.deleteZipcodeGeofence) {
                    message.success(t('wms.zipcodeMapGeofence.messages.deleteSuccess'));
                    setDeleteModalVisible(false);
                    setCurrentDeleteRecord(null);
                    reloadList();
                } else {
                    message.error(t('wms.zipcodeMapGeofence.messages.deleteFailed'));
                }
            },
            onError: (err) => {
                message.error(t('wms.zipcodeMapGeofence.messages.deleteFailedWithReason', { reason: err.message }));
            },
        }
    );

    const handleIdClick = useCallback((record: ZipcodeMapGeofenceType) => {
        setDrawerZipcode(record.zipcode);
    }, []);

    // 抽屉内可能进行了审核/删除，关闭时刷新列表
    const handleDrawerClose = useCallback(() => {
        setDrawerZipcode(null);
        reloadList();
    }, [reloadList]);

    const handleAuditClick = useCallback((record: ZipcodeMapGeofenceType) => {
        setCurrentAuditRecord(record);
        setAuditModalVisible(true);
    }, []);

    const handleDeleteClick = useCallback((record: ZipcodeMapGeofenceType) => {
        setCurrentDeleteRecord(record);
        setDeleteModalVisible(true);
    }, []);

    const columns = buildGeofenceColumns({
        t,
        showZipcodeColumn: true,
        onIdClick: handleIdClick,
        onView: handleIdClick,
        onAudit: handleAuditClick,
        onDelete: handleDeleteClick,
    });

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setTempFilters({});
        setFilters({});
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    const geofenceStatusOptions = [
        { value: 'CREATED', label: t('wms.zipcodeMapGeofence.status.created') },
        { value: 'APPROVED', label: t('wms.zipcodeMapGeofence.status.approved') },
        { value: 'EFFECTIVE', label: t('wms.zipcodeMapGeofence.status.effective') },
        { value: 'INACTIVE', label: t('wms.zipcodeMapGeofence.status.inactive') },
    ];

    const colLabels = [
        t('central.zipcodes.filters.zipcode'),
        t('central.zipcodes.filters.geofenceStatus'),
        t('central.zipcodes.filters.geofenceName'),
    ];
    const colSpanClassName = getSmartLabelClassName(colLabels);
    const statusContainerClassName = useFilterContainerClassName();
    const statusLabelClassName = useFilterLabelClassName(colSpanClassName);
    const statusInputContainerClassName = useFilterInputContainerClassName();

    return (
        <div>
            <div className="mb-2 py-2">
                <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('central.zipcodes.filters.zipcode')}
                            value={tempFilters.zipcode}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setTempFilters({
                                ...tempFilters,
                                zipcode: e.target.value || undefined,
                            })}
                            spanClassName={colSpanClassName}
                        />
                    </div>
                    <div className="min-w-0">
                        <div className={statusContainerClassName}>
                            <span className={statusLabelClassName}>
                                {t('central.zipcodes.filters.geofenceStatus')}
                            </span>
                            <div className={statusInputContainerClassName}>
                                <Select
                                    className="w-full"
                                    size="middle"
                                    allowClear
                                    placeholder={t('components.input.select')}
                                    value={tempFilters.geofenceStatus}
                                    options={geofenceStatusOptions}
                                    onChange={(value?: string) => setTempFilters({
                                        ...tempFilters,
                                        geofenceStatus: value || undefined,
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="min-w-0">
                        <InputFilterItem
                            label={t('central.zipcodes.filters.geofenceName')}
                            value={tempFilters.geofenceName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setTempFilters({
                                ...tempFilters,
                                geofenceName: e.target.value || undefined,
                            })}
                            spanClassName={colSpanClassName}
                        />
                    </div>
                </div>
            </div>

            <div className="mb-2 flex justify-end gap-2">
                <Button type="primary" onClick={handleFilter} className="w-auto min-w-24">
                    <span className="font-bold">{t('central.zipcodes.filters.filter')}</span>
                </Button>
                <Button onClick={handleResetFilters} className="w-auto min-w-24">
                    <span className="font-bold">{t('central.zipcodes.filters.reset')}</span>
                </Button>
            </div>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={listData}
                loading={loading}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="small"
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                paginationProps={{ disabled: loading }}
            />

            {/* 审核弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.audit.hint')}
                open={auditModalVisible}
                onCancel={() => { setAuditModalVisible(false); setCurrentAuditRecord(null); }}
                footer={[
                    <Button
                        key="reject"
                        danger
                        loading={auditLoading}
                        onClick={() => currentAuditRecord?.id && auditGeofence({ variables: { id: currentAuditRecord.id, result: false, level: 1 } })}
                    >
                        {t('wms.zipcodeMapGeofence.audit.rejectButton')}
                    </Button>,
                    <Button
                        key="approve"
                        type="primary"
                        loading={auditLoading}
                        onClick={() => currentAuditRecord?.id && auditGeofence({ variables: { id: currentAuditRecord.id, result: true, level: 1 } })}
                    >
                        {t('wms.zipcodeMapGeofence.audit.approveButton')}
                    </Button>,
                ]}
                width={500}
            >
                <div className="py-4">
                    <div className="mb-3 px-3 py-2 rounded bg-red-50 text-red-600 font-medium">
                        {t('wms.zipcodeMapGeofence.audit.deliveredHint', { count: currentAuditRecord?.deliveredTotal ?? 0 })}
                    </div>
                    <div className="space-y-2 text-gray-600">
                        <div className="flex items-start">
                            <span className="font-medium mr-2">{t('wms.zipcodeMapGeofence.audit.approveHint')}</span>
                            <span>{t('wms.zipcodeMapGeofence.audit.approveDesc')}</span>
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium mr-2">{t('wms.zipcodeMapGeofence.audit.rejectHint')}</span>
                            <span>{t('wms.zipcodeMapGeofence.audit.rejectDesc')}</span>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* 删除确认弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.delete.title')}
                open={deleteModalVisible}
                onCancel={() => { setDeleteModalVisible(false); setCurrentDeleteRecord(null); }}
                footer={[
                    <Button key="cancel" disabled={deleteLoading} onClick={() => { setDeleteModalVisible(false); setCurrentDeleteRecord(null); }}>
                        {t('wms.zipcodeMapGeofence.delete.cancelButton')}
                    </Button>,
                    <Button
                        key="delete"
                        type="primary"
                        danger
                        loading={deleteLoading}
                        onClick={() => currentDeleteRecord?.id && deleteGeofence({ variables: { id: currentDeleteRecord.id } })}
                    >
                        {t('wms.zipcodeMapGeofence.delete.deleteButton')}
                    </Button>,
                ]}
                width={400}
            >
                <div className="py-4">
                    <p className="text-gray-700">{t('wms.zipcodeMapGeofence.delete.confirmMessage')}</p>
                </div>
            </Modal>

            {/* 围栏黑名单详情抽屉 */}
            <ZipcodeMapDrawer
                zipcode={drawerZipcode}
                onClose={handleDrawerClose}
            />
        </div>
    );
};

export default GeofenceListTab;
